// hooks/usePolicyInsightsRuleBased.ts

import { useQuery } from "@tanstack/react-query";
import { PolicyInsightsRuleBased } from "../../interfaces/DTO/insights/PolicyInsightsRuleBased";
import { policyInsightsRuleBasedService } from "../../services/insights/policyInsightsRuleBasedService";
import { RuleCopyDTO } from "../../interfaces/DTO/insights/RuleCopyDTO";
import { RuleType } from "../../interfaces/DTO/insights/PolicyInsightsRuleBased";
import { createCachedEntityHooks } from "../createEntityHooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";


export const {
  useAll: usePolicyInsights,
  useEntityById: usePolicyInsightById,
  useEntityByIds: usePolicyInsightByIds,
  useCreateEntity: useCreatePolicyInsight,
  useUpdateEntity: useUpdatePolicyInsight,
  usePartialUpdateEntity: usePartialUpdatePolicyInsight,
  useDeleteEntity: useDeletePolicyInsight,
  useEntityListByField: useEntityListByFieldPolicyInsight,
} = createCachedEntityHooks<PolicyInsightsRuleBased>({
  entityName: "policy_insights",
  service: policyInsightsRuleBasedService,
  enableCacheById: true,
  staleTimeMs: 5000,
  fieldIndexes: {
    contact_id: () => (contact_id: number) =>
      policyInsightsRuleBasedService.fetchAllByContactId(contact_id),
    reported_period: () => (period: string) =>
      policyInsightsRuleBasedService.fetchAll(period),
  },
});
export const usePolicyInsightsByContactId = (contact_id: number) => {
  return useEntityListByFieldPolicyInsight("contact_id", contact_id);
};
export const usePolicyInsightsByReportedPeriod = (period: string) => {
  return useEntityListByFieldPolicyInsight("reported_period", period);
};

export const usePolicyInsightsByContactIdAndPeriod = (
  contactId: number,
  period: string,
  enabled: boolean = true
) => {
  return useQuery<PolicyInsightsRuleBased[]>({
    queryKey: ["policy_insights", "by-contact-and-period", contactId, period],
    queryFn: () =>
      policyInsightsRuleBasedService.fetchAllByContactIdAndPeriod(contactId, period),
    enabled: enabled && !!contactId && !!period,
    staleTime: 5000,
  });
};

export const usePolicyInsightsByContactIdsAndPeriod = (
  contactIds: number[],
  period: string,
  enabled: boolean = true
) => {
  return useQuery<PolicyInsightsRuleBased[]>({
    queryKey: ["policy_insights", "by-contacts-and-period", contactIds, period],
    queryFn: () =>
      policyInsightsRuleBasedService.fetchAllByContactIdsAndPeriod(contactIds, period),
    enabled: enabled && contactIds.length > 0 && !!period,
    staleTime: 5000,
  });
};

export const usePolicyInsightsByContactIdsPreAdvice = (
  contactIds: number[],
  enabled: boolean = true
) => {
  return useQuery<PolicyInsightsRuleBased[]>({
    queryKey: ["policy_insights", "by-contacts-pre-advice", contactIds],
    queryFn: () =>
      policyInsightsRuleBasedService.fetchAllByContactIdsPreAdvice(contactIds),
    enabled: enabled && contactIds.length > 0,
    staleTime: 5000,
  });
};
export const useRuleCopyLibrary = () => {
  return useQuery<Map<RuleType, RuleCopyDTO>>({
    queryKey: ["rule_copy_library"],
    queryFn: async () => {
      const list = await policyInsightsRuleBasedService.fetchRuleCopyLibrary();
      return new Map(list.map((item) => [item.rule_type, item]));
    },
    staleTime: Infinity,
  });
};

export const useAnalyzePolicyInsightsByPeriodAndContacts = () => {
  return useMutation({
    mutationFn: async ({
      contactIds,
      reportedPeriod,
      save = false,
    }: {
      contactIds: number[];
      reportedPeriod: string;
      save?: boolean;
    }) => {
      return policyInsightsRuleBasedService.analyzeByPeriodAndContacts(
        contactIds,
        reportedPeriod,
        save
      );
    },
  });
};

export const useAnalyzePolicyInsightsByPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportedPeriod: string) =>
      policyInsightsRuleBasedService.analyzeForCompanyByPeriod(reportedPeriod),

    onSuccess: (_data, reportedPeriod) => {
      // 🔄 invalidate כדי שה־fetchAll ירוץ מחדש
      queryClient.invalidateQueries({
        queryKey: ["policy_insights", "reported_period", reportedPeriod],
      });
    },
  });
};

export const useBulkDeletePolicyInsightsByPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportedPeriod: string) =>
      policyInsightsRuleBasedService.bulkDeleteByPeriod(reportedPeriod),

    onSuccess: (_data, reportedPeriod) => {
      queryClient.invalidateQueries({
        queryKey: ["policy_insights", "reported_period", reportedPeriod],
      });
    },
  });
};
